"use client";

import { ArrowUpRight, Blocks, BrainCircuit, Lock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function WhatIsLandClearing() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "SELECTIVE CLEARING",
    icon: Blocks,
    description: "We don't believe in scorched-earth tactics. Our process identifies and preserves high-value timber and native flora while removing invasive undergrowth. This surgical approach maintains soil integrity and immediate aesthetic value for your property development."
  }, {
    number: "02",
    title: "FORESTRY MULCHING",
    icon: BrainCircuit,
    description: "Our primary methodology utilizes high-performance mulching heads that turn debris into nutrient-rich ground cover. This eliminates the need for burn piles or hauling, creating an instant walking surface that prevents erosion and suppresses future weed growth."
  }, {
    number: "03",
    title: "SITE READINESS",
    icon: Lock,
    description: "From initial survey to final grade, we prepare your land for its next chapter. Whether you are planning a residential build, a commercial expansion, or recreational trails, our standards ensure the terrain is stable, accessible, and professionally manicured."
  }];
  return <section data-section-id="5200" id="what-is-land-clearing" className="bg-background text-foreground py-32 md:py-50 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-4">
            <div className="flex flex-col gap-3">
              <span className="font-mono text-xs md:text-sm tracking-widest uppercase text-primary">
                02 / OUR METHODOLOGY
              </span>
              <div className="h-px w-16 bg-primary" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[88px] leading-[0.95] tracking-tight uppercase">
              THE CHIPPER /
              <br />
              ADVANTAGE.
            </h2>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCard key={i} pillar={pillar} Icon={Icon} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCard({
  pillar,
  Icon,
  index
}: {
  pillar: {
    number: string;
    title: string;
    description: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 100}ms`
  }} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <Card className="group relative h-full min-h-[480px] bg-card backdrop-blur-none border border-border rounded-lg p-10 md:p-12 flex flex-col shadow-md hover:border-primary hover:shadow-xl transition-all duration-500">
        {}
        <div className="font-mono text-5xl md:text-6xl font-light text-primary transition-transform duration-500 group-hover:scale-110 origin-left">
          {pillar.number}
        </div>

        {}
        <div className="h-px w-full bg-primary/20 mt-6 mb-10 group-hover:bg-primary transition-colors duration-500" />

        {}
        <Icon className="size-8 text-secondary group-hover:text-primary transition-colors duration-500 mb-8" strokeWidth={1.5} />

        {}
        <h3 className="font-default font-bold text-2xl md:text-3xl uppercase tracking-tight mb-6 leading-tight text-foreground">
          {pillar.title}
        </h3>

        {}
        <p className="font-serif text-base md:text-[17px] leading-relaxed text-muted-foreground mb-10 flex-grow">
          {pillar.description}
        </p>

        {}
        <div className="mt-auto">
          <Button variant="ghost" size="sm" className="font-mono text-xs tracking-widest uppercase text-foreground hover:text-primary hover:bg-transparent p-0 h-auto group/btn">
            SERVICE DETAILS
            <ArrowUpRight className="size-4 text-primary transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
          </Button>
        </div>
      </Card>
    </div>;
}